import React, { useState, useEffect } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Row, Col,
} from 'reactstrap'
import Loader from 'components/common/Loader'
import PageSubtitle from 'components/common/PageSubtitle'
import { Query, UpdateSpaceInput } from 'generated/types'
import { useParams, Redirect } from 'react-router-dom'
import LayoutTitle from 'teleporters/LayoutTitle'
import PageTitle from 'components/common/PageTitle'
import PageBreadcrumbs from 'components/common/PageBreadcrumbs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Table } from 'components/common/Table'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import Divider from 'components/common/Divider'
import InfoBlock from 'components/common/InfoBlock'
import { useSession } from 'components/auth/Session'
import { DefaultDateRangePicker } from 'components/common/DateRangePicker'
import HourlyHeatMapChart from 'components/charts/HourlyHeatMapChart'
import SecondaryButton from 'components/common/SecondaryButton'
import { useLoading } from 'components/common/Loading'
import { SPACES_QUERY } from 'pages/spaces/list'
import SpaceDailyCheckinChart from './SpaceDailyCheckinChart'
import { SpacePDFLink, generateQRCode } from './Pdf'

const qrCodeWidth = 80

const StyledDownloadImg = styled.img`
  max-width: ${qrCodeWidth}px;
  margin-right: 10px;
`

const SPACE_QUERY = gql`
    query Space($id: Int!, $startDate: DateTime!, $endDate: DateTime!) {
        chart {
          hourlyUse(spaceId: $id, filters: {startDate: $startDate, endDate: $endDate}) {
            count
            hour
          }
        }
        spaces(where: {id: {equals: $id}}) {
            id
            name
            capacity
            active
            location {
                text
            }
            checkins(
              startTime: $startDate,
              endTime: $endDate,
            ) {
              id
              createdAt
              user {
                id
                name
              }
            }
            checkinSessions(
              startTime: $startDate,
              endTime: $endDate,
            ) {
                id
                startTime
                endTime
                userCount
            }
        }
    }
`

const UPDATE_SPACE_MUTATION = gql`
  mutation updateSpace($input: UpdateSpaceInput) {
    updateSpace(input: $input) {
      id
      active
    }
  }
`

export const SpacePage: React.FC = () => {
  const params = useParams<{ id: string }>()
  const { user } = useSession()
  const { withLoading } = useLoading()
  const [qrCode, setQRCode] = useState<string | undefined>(undefined)
  const [datesFilter, setDatesFilter] = useState<Array<Date | null>>(
    [DateTime.fromJSDate(new Date()).minus({ days: 14 }).toJSDate(), new Date()],
  )
  const { data, loading } = useQuery<Query>(
    SPACE_QUERY,
    {
      variables: {
        id: parseInt(params.id, 10),
        startDate: datesFilter[0]?.toISOString(),
        endDate: datesFilter[1],
      },
    },
  )

  const tenant = user?.tenant?.slug || ''
  const space = data?.spaces[0]

  const [updateSpace] = useMutation<UpdateSpaceInput>(UPDATE_SPACE_MUTATION, {
    refetchQueries: [{ query: SPACES_QUERY }],
  })

  const toggleSpaceActive = withLoading(async () => {
    if (!space) return null

    return updateSpace({
      variables: {
        input: {
          id: space.id,
          active: !space.active,
        },
      },
    })
  })

  useEffect(() => {
    // setting state on an unmounted component is frowned upon, so
    // use a useEffect cleanup to prevent the setting if the component has been unmounted
    let canceled = false
    generateQRCode({ id: params.id, tenant, width: qrCodeWidth }).then((uri) => {
      if (!canceled) {
        setQRCode(uri)
      }
    })
    return () => {
      canceled = true
    }
  }, [params.id, tenant])

  if (loading) {
    return (
      <Loader />
    )
  }

  if (!data || !space) {
    return <Redirect to="/spaces" />
  }

  const userRows = space?.checkins.map((checkin) => (
    <tr key={`session-${checkin.id}`}>
      <td>
        {DateTime.fromISO(checkin.createdAt).toLocaleString(DateTime.DATETIME_SHORT)}
      </td>
      <td>
        {checkin.user.name}
      </td>
    </tr>
  ))

  return (
    <>
      <LayoutTitle.Source>
        <PageBreadcrumbs routeTo="/spaces">
          Spaces
        </PageBreadcrumbs>
        <PageTitle><StyledDownloadImg src={qrCode} alt="space QR code" />{space.name}</PageTitle>
      </LayoutTitle.Source>

      <Row className="align-items-start">
        <Col xs="10">
          <SpacePDFLink id={space?.id.toString()!} name={space?.name!} />

          { space.active
            && (
            <SecondaryButton
              title="Archive Space"
              icon="times-circle"
              buttonProps={{ className: 'ml-3' }}
              onClick={toggleSpaceActive}
            />
            )}
          { !space.active
            && (
            <SecondaryButton
              title="Activate Space"
              icon="check-circle"
              buttonProps={{ className: 'ml-3' }}
              onClick={toggleSpaceActive}
            />
            )}
        </Col>
      </Row>

      <Row class="align-items-start">
        <Col xs="12">
          <InfoBlock
            title="Location/Building"
            subtitle={space?.location.text!}
            icon="map"
          />
          <InfoBlock
            title="Normal Capacity"
            subtitle={space?.capacity ? space.capacity.toString() : '?'}
            icon="user-plus"
          />
        </Col>
      </Row>

      <Divider />
      <Row className="mb-5">
        <Col xs="12">
          <DefaultDateRangePicker
            startDate={datesFilter[0]}
            endDate={datesFilter[1]}
            onClose={({ startDate, endDate }) => {
              setDatesFilter([startDate, endDate])
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <PageSubtitle>Count by Day</PageSubtitle>
          <SpaceDailyCheckinChart sessions={space?.checkinSessions} />
        </Col>
      </Row>

      <Row>
        <Col xs="12" className="mt-3">
          <PageSubtitle>Count by Time of Day</PageSubtitle>
          <HourlyHeatMapChart hours={data?.chart.hourlyUse} />
        </Col>
      </Row>

      <Row>
        <Col xs="12" className="mt-3">
          <PageSubtitle>Checkins</PageSubtitle>

          <Table>
            <thead>
              <tr>
                <th><FontAwesomeIcon icon="calendar" className="mr-1" /> Date</th>
                <th><FontAwesomeIcon icon="user-friends" className="mr-1" /> User</th>
              </tr>
            </thead>
            <tbody>
              {userRows}
            </tbody>
          </Table>
        </Col>
      </Row>

    </>
  )
}

export default SpacePage
